import React, { useState } from 'react';
import {
  Popover,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
} from '@mui/material';
import { useTheme } from '@mui/material';
import styles from '../ccf-app-space.module.scss';
import {
  CcfBox,
  CcfButton,
  CcfCloseIcon,
  CcfCollapsePanelIcon,
  CcfCustomizeToolbarIcon,
  CcfNewWindowIcon,
  CcfOverflowIcon,
  CcfTypography,
  useTranslator,
} from '@nice-ccf/ui-controls';
import ccfAppSpacePopoverStyles from './ccf-app-space-popover.styles';
import CcfAppHamburgerMenuForAppSpace from '../ccf-app-hamburger-menu-for-app-space/ccf-app-hamburger-menu-for-app-space';

//export type ToolTipPlacement = 'bottom-end' | 'bottom-start' | 'bottom' | 'left-end' | 'left-start' | 'left' | 'right-end' | 'right-start' | 'right' | 'top-end' | 'top-start' | 'top';

export interface CcfAppSpacePopoverProps {
  endIconComponent?: React.ReactNode;
  PaperProps?: {
    style?: { [key: string]: string };
  };
  iconComponent?: React.ReactNode;
  labelComponent?: React.ReactNode;
  anchorOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'center' | 'left' | 'right';
  };
  transformOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'center' | 'left' | 'right';
  };
  style?: {
    [key: string]: string;
  };
  tooltip?: boolean;
  setShowTooltip?: React.Dispatch<React.SetStateAction<boolean>>;
  showMenuList?: boolean;
  appSpaceMenus: any;
}

/**
 * component to display popover
 * @param param0 - CcfPopOverProps
 * @example <CcfPopOver />
 */
export function CcfAppSpacePopover({
  iconComponent = <CcfOverflowIcon className={styles.overflowIcon} />,
  endIconComponent,
  labelComponent,
  anchorOrigin = { vertical: 'center', horizontal: 'right' },
  transformOrigin = { vertical: 'top', horizontal: 'right' },
  style,
  tooltip,
  setShowTooltip,
  showMenuList,
  appSpaceMenus,
  ...rest
}: CcfAppSpacePopoverProps) {
  const theme = useTheme();
  const [showMenu, setShowMenu] = useState(appSpaceMenus);
  const [anchorElement, setAnchorElement] = React.useState(null);
  const open = Boolean(anchorElement);
  const id = open ? 'simple-popover' : undefined;
  const [translate] = useTranslator();
  const popOverStyle = ccfAppSpacePopoverStyles(theme);

  /**
   * Function to navigate to initial pop over state
   * @example navigateToInitialPopOverState()
   */
  const resetPopOver = () => {
    setShowMenu(true);
  };

  /**
   * Function to navigate to initial pop over state
   * @example navigateToInitialPopOverState()
   */
  const openCustomizePanel = () => {
    setShowMenu(false);
  };

  /**
   * Function to open pop over menu
   * @param event - any
   * @example openPopOverMenu(event)
   */
  const openPopOverMenu = (event: any) => {
    setShowMenu(true);
    setAnchorElement(event.currentTarget);
  };

  /**
   * Function to close popover
   * @example onClose()
   */
  const onClose = () => {
    setAnchorElement(null);
  };

  /**
   * Function to close popover
   * @example setTooltipFlag()
   */
  const setTooltipFlag = () => {
    setShowTooltip && setShowTooltip(false);
  };

  return (
    <>
      <Button
        id="agent_appSpace_popOverIconMenu"
        data-testid="ccf-popover-button"
        aria-controls="callControlDialog"
        aria-haspopup="true"
        onClick={openPopOverMenu}
        className="popOverOverflow"
        disableRipple
        sx={{
          gap: '7px',
          padding: 0,
          width: labelComponent ? '100%' : '24px',
          height: '40px',
          minWidth: 'auto',
          '&.MuiButtonBase-root:hover': { bgcolor: 'transparent' },
          ...popOverStyle.popOverOverflow,
        }}
      >
        {iconComponent}
        {labelComponent}
        {endIconComponent}
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorElement}
        onClose={onClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        style={style}
        onMouseEnter={setTooltipFlag}
        {...rest}
      >
        <CcfBox
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <CcfBox
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #dae2e8',
            }}
          >
            <CcfTypography variant="h5" sx={{ fontWeight: '600', marginLeft:'15px' }}>
              {translate('appSpace')}
            </CcfTypography>
            <CcfButton onClick={() => onClose()} className={styles.closeButton}>
              <CcfCloseIcon viewBox="-8 -6 32 32" fontSize="medium" />
            </CcfButton>
          </CcfBox>
          {showMenu ? (
            <List component="nav" aria-label="appSpaceMenu">
              <ListItemButton
                aria-label={'appSpaceMenu-MiniMizePanel'}
                sx={popOverStyle.popOverOptions}
                id="agent_appSpace_popOver_minimizePanel"
              >
                <ListItemIcon sx={popOverStyle.popOverOptionsIcon}>
                  <CcfCollapsePanelIcon />
                </ListItemIcon>
                <CcfTypography sx={popOverStyle.browserLabel}>
                  {translate('minimizePanel')}
                </CcfTypography>
              </ListItemButton>
              <ListItemButton
                aria-label={'appSpaceMenu-OpenNewWindow'}
                sx={popOverStyle.popOverOptions}
                id="agent_appSpace_popOverMenu_OpenNewWindow"
              >
                <ListItemIcon sx={popOverStyle.popOverOptionsIcon}>
                  <CcfNewWindowIcon />
                </ListItemIcon>
                <CcfTypography sx={popOverStyle.browserLabel}>
                  {translate('newBrowserWindow')}
                </CcfTypography>
              </ListItemButton>
              <ListItemButton
                aria-label={'appSpaceMenu-CustomizeToolbar'}
                sx={popOverStyle.popOverOptions}
                id="agent_appSpace_popOverMenu_CustomizeToolbar"
              >
                <ListItemIcon sx={popOverStyle.popOverOptionsIcon}>
                  <CcfCustomizeToolbarIcon style={{ marginLeft: '6px' }} />
                </ListItemIcon>
                <CcfTypography
                  sx={popOverStyle.browserActiveLabel}
                  onClick={openCustomizePanel}
                >
                  {translate('customIzeToolBar')}
                </CcfTypography>
              </ListItemButton>
            </List>
          ) : (
            <CcfAppHamburgerMenuForAppSpace
              helpUrl="www.google.com"
              isDrawerOpen={true}
              isAppSpaceMenu={true}
              appSpaceNavigationList={appSpaceMenus}
              toggleDrawer={resetPopOver}
            />
          )}
        </CcfBox>
      </Popover>
    </>
  );
}
export default CcfAppSpacePopover;
